import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Project } from '../resources/project/project.interface';
import { ProjectService } from '../resources/project/project.service';

/**
 * Api Core Service.
 *
 * Handles all private/core logic for the API.
 *
 * Contains a wrapper for each of the HttpClient request types.
 *
 * @note Handles logic for the V2 API only, all legacy calls go through ApiLegacyService.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiCoreService {

  constructor(
    protected httpClient: HttpClient,
    protected projectService: ProjectService,
  ) { }

  /**
   * Delete request.
   *
   * @todo Add pipe() to check for multiple response types (response, response.data)
   * @todo Return type T.
   * @todo Catch errors.
   *
   * @param url Partial url string.
   * @param [opts] Optional headers.
   * @returns API response as an observable.
   */
  protected _delete<T>(url: string, opts?: any): Observable<any> {
    return this.httpClient.delete<T>(this._parse(url), opts);
  }

  /**
   * Get request.
   *
   * @todo Add pipe() to check for multiple response types (response, response.data)
   * @todo Return type T.
   * @todo Catch errors.
   *
   * @param [url=''] Partial url string.
   * @param [opts] Optional headers.
   * @returns API response as an observable.
   */
  protected _get<T>(url: string = '', opts?: any): Observable<any> {
    return this.httpClient.get<T>(this._parse(url), opts);
  }

  /**
   * Patch request.
   *
   * @todo Add pipe() to check for multiple response types (response, response.data)
   * @todo Return type T.
   * @todo Catch errors.
   *
   * @param url Partial url string.
   * @param body Payload data.
   * @param [opts] Optional headers.
   * @returns API response as an observable.
   */
  protected _patch<T>(url: string, body: any, opts?: any): Observable<any> {
    return this.httpClient.patch<T>(this._parse(url), body, opts);
  }

  /**
   * Post request.
   *
   * @todo Add pipe() to check for multiple response types (response, response.data)
   * @todo Return type T.
   * @todo Catch errors.
   *
   * @param url Partial url string.
   * @param body Payload data.
   * @param [opts] Optional headers.
   * @returns API response as an observable.
   */
  protected _post<T>(url: string, body: any, opts?: any): Observable<any> {
    return this.httpClient.post<T>(this._parse(url), body, opts);
  }

  /**
   * Removes invalid characters from url string.
   *
   * @param url String with url parameters,
   * @returns Parsed string with prefix, and invalid characters removed.
   */
  private _parse(url: string): string {
    return `api/project/v2/${ this._getProjectId() }${ url.replace(/(\r\n|\n|\r|\s|\\n)/g, '') }`;
  }

  /**
   * Get project id.
   *
   * Because the projectService only has data *after* a project has loaded, it's not always
   * possible to get the project id, such as during the project's resolver. Examples of this
   * are the element logs and element comment counts queries.
   *
   * Normally you would take a snapshot from the Angular Router, but since that's not currently
   * available, a trick is used to get the project id anyway, though it's a little hacky.
   *
   * @todo After migration to Angular Router, replace the fallback with a Router Event snapshot.
   *
   * @returns The project's id.
   */
  private _getProjectId(): Project['_id'] {
    return this.projectService.getId()
      || location.href.split('/')[location.href.split('/').indexOf('project') + 1];
  }
}
